import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import {
  Activity, Radio, Search, ChevronRight, ChevronDown, AlertTriangle,
  CheckCircle2, XCircle, RefreshCw, Pause, Play, Building2, Code2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL as API } from '../utils/apiConfig';

const fetchJSON = async (path) => {
  const res = await fetch(`${API}${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN') : (Number(n) || 0).toLocaleString('en-IN'));

const formatTs = (ts) => {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { hour12: false });
  } catch {
    return String(ts);
  }
};

const StatusBadge = ({ status }) => {
  if (status === 'success') return <Badge className="bg-green-600 text-white text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />success</Badge>;
  if (status === 'failed' || status === 'auth_failed') return <Badge className="bg-red-600 text-white text-[10px]"><XCircle className="h-3 w-3 mr-1" />{status}</Badge>;
  if (status === 'not_found' || status === 'duplicate') return <Badge className="bg-yellow-600 text-white text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />{status}</Badge>;
  return <Badge variant="secondary" className="text-[10px]">{status}</Badge>;
};

const ReturnCodePill = ({ row }) => {
  const rc = row?.response?.ReturnCode ?? row?.response?.status_code;
  if (rc === undefined || rc === null) return null;
  const isOk = rc === 0 || rc === 200;
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${isOk ? 'bg-emerald-900/40 text-emerald-300' : 'bg-rose-900/40 text-rose-300'}`}>
      RC {String(rc)}
    </span>
  );
};

export default function APIMonitor() {
  const { user, isSuperAdmin } = useAuth();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState('');
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterEndpoint, setFilterEndpoint] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMemId, setFilterMemId] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  // Brand admin: lock to their assigned brand
  const lockedBrandId = useMemo(() => {
    if (isSuperAdmin) return null;
    return user?.brand_id ? String(user.brand_id) : null;
  }, [isSuperAdmin, user]);

  const loadBrands = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/api-monitor/brands');
      const list = data.brands || [];
      const visible = lockedBrandId
        ? list.filter(b => String(b.brand_id) === lockedBrandId)
        : list;
      setBrands(visible);
      if (!brandId && visible.length > 0) setBrandId(visible[0].brand_id);
    } catch (e) {
      console.error('Failed to load POS brands:', e);
    } finally {
      setLoadingBrands(false);
    }
  }, [lockedBrandId, brandId]);

  useEffect(() => { loadBrands(); }, [loadBrands]);

  const loadData = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filterEndpoint) params.set('endpoint', filterEndpoint);
      if (filterStatus) params.set('status', filterStatus);
      if (filterMemId) params.set('mem_id', filterMemId);
      const [recent, st] = await Promise.all([
        fetchJSON(`/api/api-monitor/${brandId}/recent?${params.toString()}`),
        fetchJSON(`/api/api-monitor/${brandId}/stats`),
      ]);
      setRows(recent.rows || []);
      setStats(st);
    } catch (e) {
      console.error('API monitor load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [brandId, filterEndpoint, filterStatus, filterMemId]);

  // Polling loop
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!brandId) return;
    loadData();
    if (autoRefresh) {
      pollRef.current = setInterval(loadData, 2500);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [brandId, autoRefresh, loadData]);

  const currentBrand = brands.find(b => String(b.brand_id) === String(brandId));

  const toggleRow = (idx) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const uniqueEndpoints = useMemo(() => {
    const s = new Set(rows.map(r => r.endpoint).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="api-monitor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="api-monitor-title">
            <Radio className="h-8 w-8 text-emerald-500 animate-pulse" />
            Live API Monitor
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Real-time request/response feed for every POS interaction. Every call from KAZO and Logic ERP flows through here.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Switch
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              data-testid="auto-refresh-toggle"
            />
            <span className="text-muted-foreground">{autoRefresh ? 'Live' : 'Paused'}</span>
            {autoRefresh ? <Play className="h-3.5 w-3.5 text-emerald-500" /> : <Pause className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading} data-testid="manual-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Brand selector + filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Brand</label>
              <Select value={String(brandId)} onValueChange={setBrandId} disabled={!!lockedBrandId || loadingBrands}>
                <SelectTrigger data-testid="brand-selector">
                  <SelectValue placeholder={loadingBrands ? 'Loading...' : 'Select a brand'} />
                </SelectTrigger>
                <SelectContent>
                  {brands.map(b => (
                    <SelectItem key={b.brand_id} value={String(b.brand_id)} data-testid={`brand-option-${b.brand_id}`}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5" />
                        <span>{b.name}</span>
                        <Badge variant="outline" className="text-[10px] ml-1">{b.provider}</Badge>
                        <span className="text-xs text-muted-foreground">· {fmt(b.today_calls)} today</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Endpoint</label>
              <Select value={filterEndpoint || 'all'} onValueChange={(v) => setFilterEndpoint(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="endpoint-filter">
                  <SelectValue placeholder="All endpoints" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All endpoints</SelectItem>
                  {uniqueEndpoints.map(ep => (
                    <SelectItem key={ep} value={ep}>{ep}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Status</label>
              <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="success">success</SelectItem>
                  <SelectItem value="failed">failed</SelectItem>
                  <SelectItem value="auth_failed">auth_failed</SelectItem>
                  <SelectItem value="not_found">not_found</SelectItem>
                  <SelectItem value="duplicate">duplicate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Customer (mobile/MemID)</label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="e.g. 9876543210"
                  value={filterMemId}
                  onChange={(e) => setFilterMemId(e.target.value)}
                  data-testid="memid-filter"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-today">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground font-medium">Today</CardTitle></CardHeader>
            <CardContent><div className="text-2xl font-bold">{fmt(stats.today_calls)}</div></CardContent>
          </Card>
          <Card data-testid="stat-total">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground font-medium">All-time</CardTitle></CardHeader>
            <CardContent><div className="text-2xl font-bold">{fmt(stats.total_calls)}</div></CardContent>
          </Card>
          <Card data-testid="stat-success">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground font-medium">Success rate</CardTitle></CardHeader>
            <CardContent><div className="text-2xl font-bold text-emerald-500">{stats.success_pct}%</div></CardContent>
          </Card>
          <Card data-testid="stat-provider">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground font-medium">Provider</CardTitle></CardHeader>
            <CardContent><div className="text-2xl font-bold capitalize">{stats.provider}</div></CardContent>
          </Card>
        </div>
      )}

      {/* Endpoint breakdown */}
      {stats?.endpoint_breakdown?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-500" />
              Endpoint heatmap
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.endpoint_breakdown.map(e => (
                <button
                  key={e.endpoint}
                  onClick={() => setFilterEndpoint(filterEndpoint === e.endpoint ? '' : e.endpoint)}
                  className={`text-xs px-2.5 py-1 rounded border ${filterEndpoint === e.endpoint ? 'bg-emerald-600/20 border-emerald-500 text-emerald-300' : 'border-border hover:border-emerald-500/50'}`}
                  data-testid={`heatmap-${e.endpoint}`}
                >
                  <span className="font-mono">{e.endpoint}</span>
                  <span className="ml-2 text-muted-foreground">{fmt(e.count)}</span>
                  {e.errors > 0 && <span className="ml-1 text-rose-400">· {e.errors} err</span>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live feed table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-emerald-500" />
              Recent calls
              <Badge variant="secondary" className="text-[10px]" data-testid="row-count">{rows.length}</Badge>
            </span>
            <span className="text-xs text-muted-foreground">
              {currentBrand ? `${currentBrand.name} · ${currentBrand.provider}` : ''}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              {loading ? 'Loading...' : 'No API calls yet. Calls will appear here as they come in.'}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left p-2 w-6"></th>
                    <th className="text-left p-2">Time</th>
                    <th className="text-left p-2">Endpoint</th>
                    <th className="text-left p-2">MemID / Mobile</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Return code</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const isOpen = expanded.has(idx);
                    return (
                      <>
                        <tr
                          key={`${r.timestamp}-${idx}`}
                          className="border-t hover:bg-muted/30 cursor-pointer"
                          onClick={() => toggleRow(idx)}
                          data-testid={`api-row-${idx}`}
                        >
                          <td className="p-2">{isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
                          <td className="p-2 font-mono text-muted-foreground">{formatTs(r.timestamp)}</td>
                          <td className="p-2 font-mono font-semibold">{r.endpoint}</td>
                          <td className="p-2 font-mono">{r.mem_id || '—'}</td>
                          <td className="p-2"><StatusBadge status={r.status} /></td>
                          <td className="p-2"><ReturnCodePill row={r} /></td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-muted/20" data-testid={`api-row-expanded-${idx}`}>
                            <td colSpan={6} className="p-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <div className="text-[10px] uppercase font-semibold text-emerald-400 mb-1">Request</div>
                                  <pre className="bg-black/40 border border-border rounded p-2 text-[11px] font-mono overflow-x-auto max-h-64 overflow-y-auto">{JSON.stringify(r.request, null, 2)}</pre>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase font-semibold text-blue-400 mb-1">Response</div>
                                  <pre className="bg-black/40 border border-border rounded p-2 text-[11px] font-mono overflow-x-auto max-h-64 overflow-y-auto">{JSON.stringify(r.response, null, 2)}</pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
